import { EditButton, Show, useDataGrid } from "@refinedev/mui";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  Link,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import SyncIcon from "@mui/icons-material/Sync";
import { IResourceComponentsProps, useCustom, useShow } from "@refinedev/core";
import { useNavigate, useParams } from "react-router-dom";
import {
  GRANT_PURPOSE_ENUM,
  GRANT_REQUEST_STATUS_ENUM,
  GRANT_REQUEST_TRANSACTION_TYPE_ENUM,
  GRANT_REQUEST_TYPE_ENUM,
  GrantPurpose,
  IGrantRequest,
  IScheduledGrantRequest,
  InvestmentInfo,
  OnTimeSchedule,
  RecurrentSchedule,
  SCHEDULED_GRANT_REQUEST_STATUS_ENUM,
} from "interfaces/grant-requests";
import { neutral, primary } from "style/color";
import { moneyFormat } from "utils/money-format";
import { shortFormat } from "utils/date-format";
import { EvaluateGrantRequestModal } from "components/modal/evaluate-grant-request";
import { useState } from "react";
import { IInvestmentRequest } from "interfaces/investment-requests";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import React from "react";
import { EvaluateScheduledGrantRequestModal } from "components/modal/evaluate-scheduled-grant-request";
import ArrowCircleDownIcon from "@mui/icons-material/ArrowCircleDown";
import { UpdateGrantRequestInfo } from "components/modal/update-grant-request-info";
import EmailIcon from "@mui/icons-material/Email";
import NotifyCharityModal from "components/modal/notify-charity-modal";
import CreateDisbursementPreviewModal from "components/modal/create-disbursement-preview-modal";
import awsExports from "aws-exports";
import { axiosInstance } from "providers/rest-data-provider/utils";

const API_BASE_URL = awsExports.endpoints[0].endpoint;

interface JournalEntryDialog {
  open: boolean;
  title: string;
  message: string;
  actionLink?: { label: string; href: string };
}

const CLOSED_JOURNAL_ENTRY_DIALOG: JournalEntryDialog = {
  open: false,
  title: "",
  message: "",
};

const SYNC_CHARITY_ACTION_LINK = {
  label: "Jump to Charities",
  href: "/charities/grantedTo",
};

export const GrantRequestDetails: React.FC<IResourceComponentsProps> = () => {
  const { id } = useParams();
  const { dataGridProps } = useDataGrid<IScheduledGrantRequest>({
    queryOptions: { enabled: false },
  });

  const { queryResult } = useShow<IGrantRequest>({
    resource: "grant-requests",
    id: id,
  });
  const { data, refetch } = queryResult;
  const record = data?.data;
  const [open, setOpen] = useState(false);
  const [openProcess, setOpenProcess] = useState(false);
  const [openUpdateInfo, setOpenUpdateInfo] = useState(false);
  const [decision, setDecision] = useState<{
    grantRequest: IGrantRequest | undefined;
    value: "PENDING" | "APPROVED" | "REJECTED" | "PROCESSED";
  }>({ grantRequest: undefined, value: "PENDING" });
  const [processDecision, setProcessDecision] = useState<{
    grantRequest: IGrantRequest | undefined;
    scheduledGrantRequest: IScheduledGrantRequest | undefined;
    value: "PENDING" | "PROCESSED";
  }>({
    grantRequest: undefined,
    scheduledGrantRequest: undefined,
    value: "PENDING",
  });
  const [openNotification, setOpenNotification] = useState(false);
  const [isSyncingJournalEntry, setIsSyncingJournalEntry] = useState(false);
  const [journalEntryDialog, setJournalEntryDialog] =
    useState<JournalEntryDialog>(CLOSED_JOURNAL_ENTRY_DIALOG);
  const [openPaymentSourceDialog, setOpenPaymentSourceDialog] =
    useState(false);
  const [isCreatingDisbursement, setIsCreatingDisbursement] = useState(false);
  const [disbursementDialog, setDisbursementDialog] =
    useState<JournalEntryDialog>(CLOSED_JOURNAL_ENTRY_DIALOG);
  const [openDisbursementPreviewDialog, setOpenDisbursementPreviewDialog] =
    useState(false);
  const [scheduledJournalEntryRow, setScheduledJournalEntryRow] =
    useState<IScheduledGrantRequest | undefined>(undefined);
  const [
    openScheduledPaymentSourceDialog,
    setOpenScheduledPaymentSourceDialog,
  ] = useState(false);
  const [isSyncingScheduledJournalEntry, setIsSyncingScheduledJournalEntry] =
    useState(false);
  const [scheduledJournalEntryDialog, setScheduledJournalEntryDialog] =
    useState<JournalEntryDialog>(CLOSED_JOURNAL_ENTRY_DIALOG);
  const [scheduledDisbursementRow, setScheduledDisbursementRow] =
    useState<IScheduledGrantRequest | undefined>(undefined);
  const [
    openScheduledDisbursementPreviewDialog,
    setOpenScheduledDisbursementPreviewDialog,
  ] = useState(false);
  const [
    isCreatingScheduledDisbursement,
    setIsCreatingScheduledDisbursement,
  ] = useState(false);
  const [scheduledDisbursementDialog, setScheduledDisbursementDialog] =
    useState<JournalEntryDialog>(CLOSED_JOURNAL_ENTRY_DIALOG);

  const handleCreateDisbursement = async () => {
    if (!record) return;
    setOpenDisbursementPreviewDialog(false);
    setIsCreatingDisbursement(true);
    try {
      const response = await axiosInstance.post(
        `${API_BASE_URL}/admin/grant-requests/${record.grantRequestId}/create-disbursement`
      );
      const result = response.data;

      if (!result.success) {
        setDisbursementDialog({
          open: true,
          title: "Create Disbursement Failed",
          message: result.message,
        });
        return;
      }

      await refetch();
      setDisbursementDialog({
        open: true,
        title: "Create Disbursement",
        message: result.message,
      });
    } catch (error: any) {
      setDisbursementDialog({
        open: true,
        title: "Create Disbursement Failed",
        message: `Create Disbursement failed: ${
          error?.response?.data?.message ?? error?.message ?? "An unexpected error occurred."
        }`,
      });
    } finally {
      setIsCreatingDisbursement(false);
    }
  };

  const disbursementPreview = React.useMemo(() => {
    if (!record) return null;
    const acknowledgement = record.acknowledgement;
    const donorSelections = acknowledgement?.donorSelections ?? [];
    return {
      charityEin: record.charity.ein,
      amount: (record.information as { appliedAmount: number }).appliedAmount,
      purposeNote: record.grantPurpose?.note,
      primaryDonorName: donorSelections[0]?.memberName,
      jointDonorName: donorSelections[1]?.memberName,
      donorAddress: acknowledgement?.donorAddress,
      fundName: acknowledgement?.showFundName
        ? record.fundAccount?.fundAccountName?.toString()
        : undefined,
    };
  }, [record]);

  const handleCreateJournalEntry = async (
    paymentSource: "investment" | "bank"
  ) => {
    if (!record) return;
    if (!record.charity.sageIntacctVendorId) {
      setOpenPaymentSourceDialog(false);
      setJournalEntryDialog({
        open: true,
        title: "Create Journal Entry Failed",
        message: `Please sync charity "${record.charity.name}" on Charities page before creating a journal entry for this grant.`,
        actionLink: SYNC_CHARITY_ACTION_LINK,
      });
      return;
    }
    setOpenPaymentSourceDialog(false);
    setIsSyncingJournalEntry(true);
    try {
      const response = await axiosInstance.post(
        `${API_BASE_URL}/admin/grant-requests/${record.grantRequestId}/sync-journal-entry`,
        { paymentSource }
      );
      const result = response.data;

      if (!result.success) {
        setJournalEntryDialog({
          open: true,
          title: "Create Journal Entry Failed",
          message: `Create Journal Entry request failed: ${result.message}`,
        });
        return;
      }

      await refetch();
      setJournalEntryDialog({
        open: true,
        title: "Create Journal Entry",
        message: "Journal entry has been successfully created for this grant.",
      });
    } catch (error: any) {
      setJournalEntryDialog({
        open: true,
        title: "Create Journal Entry Failed",
        message: `Create Journal Entry request failed: ${
          error?.response?.data?.message ?? error?.message ?? "An unexpected error occurred."
        }`,
      });
    } finally {
      setIsSyncingJournalEntry(false);
    }
  };

  const handleCreateScheduledJournalEntry = async (
    paymentSource: "investment" | "bank"
  ) => {
    const row = scheduledJournalEntryRow;
    // The charity/fund live on the parent grant request, not on the leg
    // itself (`row.grantRequest` isn't populated when the leg is loaded as
    // part of the parent's own list — every leg belongs to this `record`).
    if (!row || !record) return;
    if (!record.charity.sageIntacctVendorId) {
      setOpenScheduledPaymentSourceDialog(false);
      setScheduledJournalEntryDialog({
        open: true,
        title: "Create Journal Entry Failed",
        message: `Please sync charity "${record.charity.name}" on Charities page before creating a journal entry for this grant.`,
        actionLink: SYNC_CHARITY_ACTION_LINK,
      });
      return;
    }
    setOpenScheduledPaymentSourceDialog(false);
    setIsSyncingScheduledJournalEntry(true);
    try {
      const response = await axiosInstance.post(
        `${API_BASE_URL}/admin/scheduled-grant-requests/${row.scheduledGrantRequestId}/sync-journal-entry`,
        { paymentSource }
      );
      const result = response.data;

      if (!result.success) {
        setScheduledJournalEntryDialog({
          open: true,
          title: "Create Journal Entry Failed",
          message: `Create Journal Entry request failed: ${result.message}`,
        });
        return;
      }

      await refetch();
      setScheduledJournalEntryDialog({
        open: true,
        title: "Create Journal Entry",
        message: "Journal entry has been successfully created for this transaction.",
      });
    } catch (error: any) {
      setScheduledJournalEntryDialog({
        open: true,
        title: "Create Journal Entry Failed",
        message: `Create Journal Entry request failed: ${
          error?.response?.data?.message ?? error?.message ?? "An unexpected error occurred."
        }`,
      });
    } finally {
      setIsSyncingScheduledJournalEntry(false);
      setScheduledJournalEntryRow(undefined);
    }
  };

  const handleCreateScheduledDisbursement = async () => {
    const row = scheduledDisbursementRow;
    if (!row) return;
    setOpenScheduledDisbursementPreviewDialog(false);
    setIsCreatingScheduledDisbursement(true);
    try {
      const response = await axiosInstance.post(
        `${API_BASE_URL}/admin/scheduled-grant-requests/${row.scheduledGrantRequestId}/create-disbursement`
      );
      const result = response.data;

      if (!result.success) {
        setScheduledDisbursementDialog({
          open: true,
          title: "Create Disbursement Failed",
          message: result.message,
        });
        return;
      }

      await refetch();
      setScheduledDisbursementDialog({
        open: true,
        title: "Create Disbursement",
        message: result.message,
      });
    } catch (error: any) {
      setScheduledDisbursementDialog({
        open: true,
        title: "Create Disbursement Failed",
        message: `Create Disbursement failed: ${
          error?.response?.data?.message ?? error?.message ?? "An unexpected error occurred."
        }`,
      });
    } finally {
      setIsCreatingScheduledDisbursement(false);
      setScheduledDisbursementRow(undefined);
    }
  };

  // The charity/fund/acknowledgement/purpose live on the parent grant
  // request (`record`), not on the leg — a recurring grant's donor and
  // purpose info applies to the whole series, not to any one execution.
  // Only the amount comes from the leg itself.
  const scheduledDisbursementPreview = React.useMemo(() => {
    if (!record || !scheduledDisbursementRow) return null;
    const acknowledgement = record.acknowledgement;
    const donorSelections = acknowledgement?.donorSelections ?? [];
    return {
      charityEin: record.charity.ein,
      amount: scheduledDisbursementRow.appliedAmount,
      purposeNote: record.grantPurpose?.note,
      primaryDonorName: donorSelections[0]?.memberName,
      jointDonorName: donorSelections[1]?.memberName,
      donorAddress: acknowledgement?.donorAddress,
      fundName: acknowledgement?.showFundName
        ? record.fundAccount?.fundAccountName?.toString()
        : undefined,
    };
  }, [record, scheduledDisbursementRow]);

  const columns = React.useMemo<GridColDef[]>(
    () => [
      {
        field: "scheduledGrantRequestId",
        flex: 1,
        headerName: "ID",
        minWidth: 80,
        renderCell: function render({ value }: any) {
          return value.scheduledGrantRequestId;
        },
      },

      {
        field: "status",
        flex: 1,
        headerName: "status",
        minWidth: 100,
      },
      {
        field: "executionDate",
        flex: 1,
        headerName: "executionDate",
        minWidth: 100,
        renderCell: function render({ value }: any) {
          return shortFormat(value);
        },
      },
      {
        field: "actions",
        headerName: "Actions",
        sortable: false,
        renderCell: function render({ row }) {
          const MIN_CELL_HEIGHT = 52;

          if (row.status === SCHEDULED_GRANT_REQUEST_STATUS_ENUM.APPROVED) {
            return (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: MIN_CELL_HEIGHT,
                }}
              >
                <Button
                  size="medium"
                  variant="contained"
                  style={{ marginRight: "0.5rem" }}
                  color="primary"
                  disabled={!row.availableBalanceUpdated}
                  sx={{ fontSize: "12px" }}
                  onClick={() => {
                    setOpenProcess(true);
                    setProcessDecision({
                      grantRequest: record,
                      scheduledGrantRequest: row,
                      value: "PROCESSED",
                    });
                  }}
                >
                  Process
                </Button>
              </Box>
            );
          }
          // Journal Entry / Disbursement creation for individual legs only
          // applies to recurring grants — a one-time grant already has its
          // own header-level buttons, scoped to the whole grant request
          // rather than a single transaction. Both buttons are independent:
          // a leg can need one, the other, both, or neither.
          if (
            row.status !== SCHEDULED_GRANT_REQUEST_STATUS_ENUM.PROCESSED ||
            record?.type !== GRANT_REQUEST_TYPE_ENUM.RECURRENT
          ) {
            return (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: MIN_CELL_HEIGHT,
                }}
              >
                --
              </Box>
            );
          }

          const buttons: React.ReactNode[] = [];

          if (!row.sageIntacctTransactionId) {
            buttons.push(
              <Button
                key="journal-entry"
                size="medium"
                variant="outlined"
                color="primary"
                sx={{ fontSize: "12px" }}
                startIcon={
                  isSyncingScheduledJournalEntry &&
                  scheduledJournalEntryRow?.scheduledGrantRequestId ===
                    row.scheduledGrantRequestId ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : undefined
                }
                disabled={isSyncingScheduledJournalEntry}
                onClick={() => {
                  setScheduledJournalEntryRow(row);
                  if (!record?.charity.sageIntacctVendorId) {
                    setScheduledJournalEntryDialog({
                      open: true,
                      title: "Create Journal Entry Failed",
                      message: `Please sync charity "${record?.charity.name}" on Charities page before creating a journal entry for this grant.`,
                      actionLink: SYNC_CHARITY_ACTION_LINK,
                    });
                    return;
                  }
                  setOpenScheduledPaymentSourceDialog(true);
                }}
              >
                Create Journal Entry
              </Button>
            );
          }

          if (!row.chariotDisbursementId) {
            buttons.push(
              <Button
                key="disbursement"
                size="medium"
                variant="outlined"
                color="primary"
                sx={{ fontSize: "12px" }}
                startIcon={
                  isCreatingScheduledDisbursement &&
                  scheduledDisbursementRow?.scheduledGrantRequestId ===
                    row.scheduledGrantRequestId ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : undefined
                }
                disabled={isCreatingScheduledDisbursement}
                onClick={() => {
                  setScheduledDisbursementRow(row);
                  setOpenScheduledDisbursementPreviewDialog(true);
                }}
              >
                Create Disbursement
              </Button>
            );
          }

          if (buttons.length === 0) {
            return (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: MIN_CELL_HEIGHT,
                }}
              >
                --
              </Box>
            );
          }

          return (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.5,
                py: 1,
                minHeight: MIN_CELL_HEIGHT,
              }}
            >
              {buttons}
            </Box>
          );
        },
        align: "center",
        headerAlign: "center",
        minWidth: 200,
      },
      {
        field: "sageIntacctTransactionId",
        headerName: "SI Transaction ID",
        sortable: false,
        renderCell: function render({ row }) {
          return row.sageIntacctTransactionId ?? "--";
        },
        align: "center",
        headerAlign: "center",
        minWidth: 160,
      },
      {
        field: "chariotDisbursementId",
        headerName: "Chariot Disbursement ID",
        sortable: false,
        renderCell: function render({ row }) {
          const disbursementId: string | null | undefined =
            row.chariotDisbursementId;
          if (!disbursementId) return "--";

          const abbreviated =
            disbursementId.length > 16
              ? `${disbursementId.slice(0, 8)}...${disbursementId.slice(-4)}`
              : disbursementId;

          return (
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
            >
              <Tooltip title={disbursementId}>
                <Typography variant="body2" component="span">
                  {abbreviated}
                </Typography>
              </Tooltip>
              <Tooltip title="Copy full ID">
                <IconButton
                  size="small"
                  onClick={() => {
                    navigator.clipboard.writeText(disbursementId);
                  }}
                >
                  <ContentCopyIcon sx={{ fontSize: "14px" }} />
                </IconButton>
              </Tooltip>
            </Box>
          );
        },
        align: "center",
        headerAlign: "center",
        minWidth: 160,
      },
      {
        field: "receipt",
        headerName: "Receipt",
        sortable: false,
        renderCell: function render({ row }) {
          return row.status === SCHEDULED_GRANT_REQUEST_STATUS_ENUM.PROCESSED ||
            SCHEDULED_GRANT_REQUEST_STATUS_ENUM.APPROVED ||
            SCHEDULED_GRANT_REQUEST_STATUS_ENUM.PENDING
            ? row.receipt && (
              <Link
                href={row.receipt?.url}
                target="__blank"
                underline="none"
                color="inherit"
              >
                <ArrowCircleDownIcon />
              </Link>
            )
            : "--";
        },
        align: "center",
        headerAlign: "center",
        minWidth: 80,
      },
      {
        field: "Notify",
        headerName: "Notify",
        sortable: false,
        renderCell: function render({ row }) {
          return row.status === SCHEDULED_GRANT_REQUEST_STATUS_ENUM.PROCESSED ||
            SCHEDULED_GRANT_REQUEST_STATUS_ENUM.APPROVED ||
            SCHEDULED_GRANT_REQUEST_STATUS_ENUM.PENDING
            ? row.receipt && (
              <Button
                startIcon={<EmailIcon />}
                variant="text"
                sx={{ ml: 1 }}
                onClick={() => setOpenNotification(true)}
              ></Button>
            )
            : "--";
        },
        align: "center",
        headerAlign: "center",
        minWidth: 80,
      },
    ],
    // `record` must stay in this list: the Journal Entry column above reads
    // record?.type / record?.charity, and without it those closures would
    // stay frozen at whatever `record` was on the very first render (likely
    // undefined, before the grant request has loaded).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [record]
  );
  window.history.replaceState(null, "");
  const { data: investmentRequestData, isLoading } = useCustom({
    url: `investments-requests`,
    method: "get",
    config: {
      query: {},
      filters: [
        {
          field: "filter[grantRequestId]",
          operator: "eq",
          value: record?.grantRequestId,
        },
      ],
    },
    queryOptions: {
      enabled: !!record?.grantRequestId,
    },
  });
  const navigate = useNavigate();
  return (
    <Show
      goBack={
        <Button onClick={() => navigate(-1)}>
          <ArrowBackIcon />
        </Button>
      }
      isLoading={queryResult.isLoading}
      title={
        <Typography variant="h5" color={primary}>
          Grant Request Details
        </Typography>
      }
      headerButtons={
        !(
          record?.status === GRANT_REQUEST_STATUS_ENUM.REJECTED ||
          record?.status === GRANT_REQUEST_STATUS_ENUM.CANCELED
        ) && <EditButton onClick={() => setOpenUpdateInfo(true)} />
      }
    >
      {!queryResult.isLoading && (
        <>
          {" "}
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",

              justifyContent: "flex-end",
              marginTop: "20px",
            }}
          >
            {" "}
            {record?.type === GRANT_REQUEST_TYPE_ENUM.ONETIME &&
              record?.status === GRANT_REQUEST_STATUS_ENUM.PROCESSED &&
              !record?.chariotDisbursementId && (
                <Button
                  startIcon={
                    isCreatingDisbursement ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <SyncIcon />
                    )
                  }
                  variant="contained"
                  sx={{ mr: 1 }}
                  disabled={isCreatingDisbursement}
                  onClick={() => setOpenDisbursementPreviewDialog(true)}
                  className="grant-request-create-disbursement"
                >
                  Create Disbursement
                </Button>
              )}
            {record?.type === GRANT_REQUEST_TYPE_ENUM.ONETIME &&
              record?.status === GRANT_REQUEST_STATUS_ENUM.PROCESSED &&
              !record?.sageIntacctTransactionId && (
                <Button
                  startIcon={
                    isSyncingJournalEntry ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <SyncIcon />
                    )
                  }
                  variant="contained"
                  sx={{ mr: 1 }}
                  disabled={isSyncingJournalEntry}
                  onClick={() => {
                    if (!record.charity.sageIntacctVendorId) {
                      setJournalEntryDialog({
                        open: true,
                        title: "Create Journal Entry Failed",
                        message: `Please sync charity "${record.charity.name}" on Charities page before creating a journal entry for this grant.`,
                        actionLink: SYNC_CHARITY_ACTION_LINK,
                      });
                      return;
                    }
                    setOpenPaymentSourceDialog(true);
                  }}
                  className="grant-request-create-journal-entry"
                >
                  Create Journal Entry
                </Button>
              )}
            {record?.status &&
              [
                GRANT_REQUEST_STATUS_ENUM.PROCESSED,
                GRANT_REQUEST_STATUS_ENUM.APPROVED,
                GRANT_REQUEST_STATUS_ENUM.PENDING,
              ].includes(record?.status) &&
              record?.scheduledGrantRequests &&
              record.scheduledGrantRequests[0].receipt && (
                <Link
                  href={record?.scheduledGrantRequests[0]?.receipt?.url}
                  target="__blank"
                  underline="none"
                  color="inherit"
                >
                  {" "}
                  <Button
                    fullWidth
                    startIcon={<ArrowCircleDownIcon />}
                    variant="contained"
                  >
                    Download
                  </Button>{" "}
                </Link>
              )}
            {/* If more then one scheduled grant request and the first one is
            processed, show notify charity button else it will appear on the
            table of requests */}
            {record?.type === GRANT_REQUEST_TYPE_ENUM.ONETIME &&
              record?.scheduledGrantRequests?.[0]?.status ===
              SCHEDULED_GRANT_REQUEST_STATUS_ENUM.PROCESSED && (
                <Button
                  startIcon={<EmailIcon />}
                  variant="contained"
                  sx={{ ml: 1 }}
                  onClick={() => setOpenNotification(true)}
                >
                  Notify Charity
                </Button>
              )}
          </Box>
          <Grid container spacing={1}>
            <Grid item xs={12} md={6}>
              <TextField
                value={`${record?.user?.title ? record?.user?.title : ""} ${record?.user?.firstName ? record?.user?.firstName : ""
                  } ${record?.user?.middleName ? record?.user?.middleName : ""} ${record?.user?.lastName ? record.user?.lastName : ""
                  } ${record?.user?.suffix ? record?.user?.suffix : ""}`}
                margin="normal"
                fullWidth
                InputLabelProps={{ shrink: true }}
                type="text"
                label="User "
                disabled
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                disabled
                value={record?.fundAccount?.fundAccountName}
                margin="normal"
                fullWidth
                InputLabelProps={{ shrink: true }}
                type="text"
                label="Fund Name"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                disabled
                value={record?.charity.ein}
                margin="normal"
                fullWidth
                InputLabelProps={{ shrink: true }}
                type="text"
                label="Charity Ein"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                disabled
                value={record?.charity.name}
                margin="normal"
                fullWidth
                InputLabelProps={{ shrink: true }}
                type="text"
                label="Charity Name"
              />
            </Grid>
            {record?.type === GRANT_REQUEST_TYPE_ENUM.ONETIME &&
              record?.sageIntacctTransactionId && (
                <Grid item xs={12} md={6}>
                  <TextField
                    disabled
                    value={record.sageIntacctTransactionId}
                    margin="normal"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    type="text"
                    label="Sage Intacct Transaction ID"
                  />
                </Grid>
              )}
            {record?.type === GRANT_REQUEST_TYPE_ENUM.ONETIME &&
              record?.chariotDisbursementId && (
                <Grid item xs={12} md={6}>
                  <TextField
                    disabled
                    value={record.chariotDisbursementId}
                    margin="normal"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    type="text"
                    label="Chariot Disbursement ID"
                  />
                </Grid>
              )}
            <Grid item xs={12} md={6}>
              <TextField
                disabled
                value={record?.type}
                margin="normal"
                fullWidth
                InputLabelProps={{ shrink: true }}
                type="text"
                label="Grant Type"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                disabled
                value={record?.status}
                margin="normal"
                fullWidth
                InputLabelProps={{ shrink: true }}
                type="text"
                label="Request status"
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="bodyCopyBold" color={primary}>
                Requested{" "}
                <Typography variant="bodyCopyRegular" color={primary[500]}>
                  {" "}
                  {record?.transactionType}{" "}
                </Typography>
                information
              </Typography>
            </Grid>

            {record?.transactionType &&
              record.transactionType ===
              GRANT_REQUEST_TRANSACTION_TYPE_ENUM.INVESTMENT && (
                <>
                  <Grid item xs={12} md={6}>
                    <TextField
                      disabled
                      value={(record.information as InvestmentInfo).ticker}
                      margin="normal"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      type="text"
                      label="Ticker"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      disabled
                      value={
                        (record.information as InvestmentInfo).interestedIn
                      }
                      margin="normal"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      type="text"
                      label="Interested In "
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      disabled
                      value={moneyFormat(
                        (record.information as InvestmentInfo).units,
                        4
                      )}
                      margin="normal"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      type="text"
                      label="Units"
                    />{" "}
                  </Grid>
                </>
              )}
            <Grid item xs={12} md={6}>
              <TextField
                disabled
                value={`$${moneyFormat(
                  record?.information.requestedAmount || 0
                )}`}
                margin="normal"
                fullWidth
                InputLabelProps={{ shrink: true }}
                type="text"
                label="Requested Amount"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                disabled
                value={record?.emailToNotify}
                margin="normal"
                fullWidth
                InputLabelProps={{ shrink: true }}
                type="text"
                label="Email to notify"
              />
            </Grid>

            {record?.status === GRANT_REQUEST_STATUS_ENUM.PROCESSED && (
              <Grid item xs={12} md={6}>
                <TextField
                  disabled
                  value={`$${moneyFormat(record?.information.appliedAmount)}`}
                  margin="normal"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  type="text"
                  label="Applied Amount"
                />{" "}
              </Grid>
            )}

            <Grid item xs={12} md={6}>
              <TextField
                disabled
                value={`$${moneyFormat(
                  record?.information.appliedAmount || 0
                )}`}
                margin="normal"
                fullWidth
                InputLabelProps={{ shrink: true }}
                type="text"
                label="Applied Amount"
              />{" "}
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                disabled
                value={record?.toInform}
                margin="normal"
                fullWidth
                InputLabelProps={{ shrink: true }}
                type="text"
                label="Person to inform"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                disabled
                value={shortFormat(record?.createdAt)}
                margin="normal"
                fullWidth
                InputLabelProps={{ shrink: true }}
                type="text"
                label="Creation date"
              />
            </Grid>
            {record?.status === GRANT_REQUEST_STATUS_ENUM.APPROVED && (
              <Grid item xs={12} md={6}>
                <TextField
                  disabled
                  value={shortFormat(record?.updatedAt)}
                  margin="normal"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  type="text"
                  label="Approval date"
                />
              </Grid>
            )}
            {record?.status === GRANT_REQUEST_STATUS_ENUM.PROCESSED && (
              <Grid item xs={12} md={6}>
                {record.type === GRANT_REQUEST_TYPE_ENUM.ONETIME && (
                  <TextField
                    disabled
                    value={shortFormat(
                      (record?.scheduleInformation as OnTimeSchedule)
                        .executionDate
                    )}
                    margin="normal"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    type="text"
                    label="Process date"
                  />
                )}
              </Grid>
            )}
            <Grid item xs={12}>
              <Typography variant="bodyCopyBold" color={primary}>
                Request type is
                <Typography variant="bodyCopyRegular" color={primary[500]}>
                  {" "}
                  {record?.type}{" "}
                </Typography>
              </Typography>
            </Grid>
            {record?.type === GRANT_REQUEST_TYPE_ENUM.RECURRENT &&
              (record?.scheduleInformation as RecurrentSchedule).startingOn && (
                <Grid item xs={12}>
                  <Typography variant="bodyCopyRegular" color={primary[500]}>
                    Start date{" "}
                    {shortFormat(
                      (record?.scheduleInformation as RecurrentSchedule)
                        .startingOn
                    )}
                  </Typography>
                </Grid>
              )}
            {record?.type === GRANT_REQUEST_TYPE_ENUM.RECURRENT &&
              (record?.scheduleInformation as RecurrentSchedule).endingOn && (
                <Grid item xs={12}>
                  <Typography variant="bodyCopyRegular" color={primary[500]}>
                    End date{" "}
                    {shortFormat(
                      (record?.scheduleInformation as RecurrentSchedule)
                        .endingOn
                    )}
                  </Typography>
                </Grid>
              )}

            {record?.type === GRANT_REQUEST_TYPE_ENUM.RECURRENT &&
              (record?.scheduleInformation as RecurrentSchedule)
                .nextExecutionDate && (
                <Grid item xs={12}>
                  <Typography variant="bodyCopyRegular" color={primary[500]}>
                    Next execution date{" "}
                    {shortFormat(
                      (record?.scheduleInformation as RecurrentSchedule)
                        .nextExecutionDate
                    )}
                  </Typography>
                </Grid>
              )}
            {record?.type === GRANT_REQUEST_TYPE_ENUM.RECURRENT &&
              (record?.scheduleInformation as RecurrentSchedule)
                .lastExecutionDate && (
                <Grid item xs={12}>
                  <Typography variant="bodyCopyRegular" color={primary[500]}>
                    Previous execution date{" "}
                    {shortFormat(
                      (record?.scheduleInformation as RecurrentSchedule)
                        .lastExecutionDate
                    )}
                  </Typography>
                </Grid>
              )}
            {record?.type === GRANT_REQUEST_TYPE_ENUM.RECURRENT &&
              (record?.scheduleInformation as RecurrentSchedule).frequency && (
                <Grid item xs={12}>
                  <Typography variant="bodyCopyRegular" color={primary[500]}>
                    Execution frequency{" "}
                    {
                      (record?.scheduleInformation as RecurrentSchedule)
                        .frequency
                    }
                  </Typography>
                </Grid>
              )}
            {record?.type === GRANT_REQUEST_TYPE_ENUM.RECURRENT &&
              (
                record.scheduleInformation as RecurrentSchedule
              ).executionDates.map((date) => (
                <Grid item xs={12} md={3}>
                  <Typography variant="bodyCopyRegular" color={primary[500]}>
                    {shortFormat(date)}
                  </Typography>
                </Grid>
              ))}
            {record?.type === GRANT_REQUEST_TYPE_ENUM.ONETIME && (
              <Grid item xs={12}>
                <Typography color={primary}>
                  The Grant request must be executed:
                  <Typography variant="bodyCopyRegular" color={primary[500]}>
                    {(record.scheduleInformation as OnTimeSchedule).now
                      ? "immediately"
                      : shortFormat(
                        (record.scheduleInformation as OnTimeSchedule)
                          .executionDate
                      )}
                  </Typography>
                </Typography>
              </Grid>
            )}
            {((record?.type === GRANT_REQUEST_TYPE_ENUM.ONETIME &&
              !(record.scheduleInformation as OnTimeSchedule).now) ||
              record?.type === GRANT_REQUEST_TYPE_ENUM.RECURRENT) &&
              record?.status !== GRANT_REQUEST_STATUS_ENUM.PENDING && (
                <>
                  <Grid item xs={12}>
                    <EvaluateScheduledGrantRequestModal
                      visible={openProcess}
                      close={setOpenProcess}
                      refresh={refetch}
                      data={processDecision}
                    />
                    <Typography variant="bodyCopyBold" color={primary}>
                      Transaction List{" "}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    {record.scheduledGrantRequests?.length !== 0 && (
                      <DataGrid
                        {...dataGridProps}
                        rows={record.scheduledGrantRequests as any}
                        getRowId={(row) => row.scheduledGrantRequestId}
                        columns={columns}
                        autoHeight
                        getRowHeight={() => "auto"}
                        loading={isLoading}
                      />
                    )}
                    <Typography
                      variant="smallCopyBold"
                      sx={{ color: neutral[600] }}
                    >
                      *Transactions can only be processed until the fund's
                      available balance has been updated.
                    </Typography>
                  </Grid>
                </>
              )}
            {record?.acknowledgement && (
              <>
                <Grid item xs={12}>
                  <Typography variant="bodyCopyBold" color={primary}>
                    Acknowledgment information{" "}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={record.acknowledgement.anonymous}
                        disabled
                      />
                    }
                    label="User want to be anonyms"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={record.acknowledgement.visibility}
                        disabled
                      />
                    }
                    label="Visibility"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={record.acknowledgement.showFundName}
                        disabled
                      />
                    }
                    label="Show Fund Name"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(
                          record.acknowledgement.showDonorAddress ??
                            record.acknowledgement.showDonorAdress,
                        )}
                        disabled
                      />
                    }
                    label="Show Donner Address"
                  />
                </Grid>
                {record?.acknowledgement.inHonorOf && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      disabled
                      value={record?.acknowledgement.inHonorOf}
                      margin="normal"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      type="text"
                      label="In Honor of"
                    />
                  </Grid>
                )}
                {record?.acknowledgement.inMemoryOf && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      disabled
                      value={record?.acknowledgement.inMemoryOf}
                      margin="normal"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      type="text"
                      label="In Memory of"
                    />
                  </Grid>
                )}
                <Grid item xs={12} md={6}>
                  <TextField
                    disabled
                    value={record?.grantPurpose?.type}
                    margin="normal"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    type="text"
                    label="Grant Purpose Type"
                  />
                </Grid>
                {record?.grantPurpose?.type === GRANT_PURPOSE_ENUM.SPECIFIC &&
                  record.grantPurpose.note && (
                    <Grid item xs={12} md={6}>
                      <TextField
                        disabled
                        value={record.grantPurpose?.note}
                        margin="normal"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        type="text"
                        label="Grant Purpose Note"
                      />
                    </Grid>
                  )}
                {record?.acknowledgement.thirdPartiesEmails &&
                  record?.acknowledgement.thirdPartiesEmails.length > 0 && (
                    <>
                      <Grid item xs={12}>
                        <Typography variant="bodyCopyBold" color={primary}>
                          Third Parties Emails
                        </Typography>
                      </Grid>
                      {record?.acknowledgement.thirdPartiesEmails.map(
                        (email) => (
                          <Grid item xs={12} md={6}>
                            <TextField
                              disabled
                              value={email}
                              margin="normal"
                              fullWidth
                              InputLabelProps={{ shrink: true }}
                              type="text"
                            />
                          </Grid>
                        )
                      )}
                    </>
                  )}
              </>
            )}
            {record?.rejectionReason && (
              <Grid item xs={12} md={6}>
                <TextField
                  disabled
                  value={record.rejectionReason}
                  margin="normal"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  type="text"
                  label="Rejection reason"
                />
              </Grid>
            )}
            {!isLoading &&
              investmentRequestData &&
              investmentRequestData?.data.data.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="bodyCopyBold" color={primary}>
                    Related investment requests{" "}
                  </Typography>
                </Grid>
              )}
            {investmentRequestData &&
              investmentRequestData?.data.data.length > 0 &&
              investmentRequestData?.data.data.map(
                (invReq: IInvestmentRequest, index: number) => (
                  <Grid item xs={12} md={6}>
                    <Typography color={primary}>
                      {index + 1}- The{" "}
                      <Link
                        onClick={() =>
                          navigate(
                            `/investment-requests/${invReq.status.toLowerCase()}/${invReq.investmentRequestId
                            }`,
                            {
                              replace: true,
                            }
                          )
                        }
                        target="_blank"
                        underline="always"
                        color={"inherit"}
                        sx={{
                          "&:hover": {
                            cursor: "pointer",
                          },
                        }}
                      >
                        request
                      </Link>{" "}
                      status is{" "}
                      <Typography
                        variant="bodyCopyRegular"
                        color={primary[500]}
                      >
                        {" "}
                        {invReq?.status}
                      </Typography>
                    </Typography>
                  </Grid>
                )
              )}

            <Grid item xs={12} md={12} textAlign={"end"}>
              <EvaluateGrantRequestModal
                visible={open}
                close={setOpen}
                data={decision}
              />
              <UpdateGrantRequestInfo
                open={openUpdateInfo}
                refetch={refetch}
                setOpen={setOpenUpdateInfo}
                id={record?.grantRequestId as number}
                toInform={record?.toInform}
                grantPurpose={record?.grantPurpose as GrantPurpose}
              />
              {record?.status === "PENDING" && (
                <>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",

                      justifyContent: "flex-end",
                      marginTop: "20px",
                    }}
                  >
                    <Button
                      size="large"
                      variant="contained"
                      style={{ marginRight: "0.5rem" }}
                      color="primary"
                      onClick={() => {
                        setOpen(true);
                        setDecision({
                          grantRequest: record,
                          value: "APPROVED",
                        });
                      }}
                    >
                      Accept
                    </Button>
                    <Button
                      size="large"
                      variant="outlined"
                      color="primary"
                      onClick={() => {
                        setOpen(true);
                        setDecision({
                          grantRequest: record,
                          value: "REJECTED",
                        });
                      }}
                    >
                      Reject
                    </Button>
                  </Box>
                </>
              )}

              {record?.status === "APPROVED" && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",

                    justifyContent: "flex-end",
                    marginTop: "20px",
                  }}
                >
                  <Button
                    size="large"
                    variant="contained"
                    style={{ marginRight: "0.5rem" }}
                    color="primary"
                    onClick={() => {
                      setOpen(true);
                      setDecision({
                        grantRequest: record,
                        value: "PROCESSED",
                      });
                    }}
                  >
                    Process
                  </Button>
                </Box>
              )}
            </Grid>
          </Grid>{" "}
        </>
      )}
      <NotifyCharityModal
        open={openNotification}
        setOpen={setOpenNotification}
        scheduledGrantRequestId={
          record?.scheduledGrantRequests?.[0]?.scheduledGrantRequestId as number
        }
        charityEin={record?.charity.ein as string}
        fundAccountId={record?.fundAccount.fundAccountId as number}
      />
      <Dialog
        open={journalEntryDialog.open}
        onClose={() => setJournalEntryDialog(CLOSED_JOURNAL_ENTRY_DIALOG)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{journalEntryDialog.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>{journalEntryDialog.message}</DialogContentText>
        </DialogContent>
        <DialogActions>
          {journalEntryDialog.actionLink && (
            <Button
              onClick={() => navigate(journalEntryDialog.actionLink!.href)}
            >
              {journalEntryDialog.actionLink.label}
            </Button>
          )}
          <Button
            onClick={() => setJournalEntryDialog(CLOSED_JOURNAL_ENTRY_DIALOG)}
            autoFocus
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openPaymentSourceDialog}
        onClose={() => setOpenPaymentSourceDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create Journal Entry</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Which account should this grant be paid from?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPaymentSourceDialog(false)}>
            Cancel
          </Button>
          <Button
            variant="outlined"
            onClick={() => handleCreateJournalEntry("investment")}
          >
            Pay Directly from Investment Account
          </Button>
          <Button
            variant="contained"
            onClick={() => handleCreateJournalEntry("bank")}
          >
            Route through Bank Account
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={scheduledJournalEntryDialog.open}
        onClose={() =>
          setScheduledJournalEntryDialog(CLOSED_JOURNAL_ENTRY_DIALOG)
        }
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{scheduledJournalEntryDialog.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {scheduledJournalEntryDialog.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          {scheduledJournalEntryDialog.actionLink && (
            <Button
              onClick={() =>
                navigate(scheduledJournalEntryDialog.actionLink!.href)
              }
            >
              {scheduledJournalEntryDialog.actionLink.label}
            </Button>
          )}
          <Button
            onClick={() =>
              setScheduledJournalEntryDialog(CLOSED_JOURNAL_ENTRY_DIALOG)
            }
            autoFocus
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openScheduledPaymentSourceDialog}
        onClose={() => setOpenScheduledPaymentSourceDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create Journal Entry</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Which account should this grant be paid from?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenScheduledPaymentSourceDialog(false)}>
            Cancel
          </Button>
          <Button
            variant="outlined"
            onClick={() => handleCreateScheduledJournalEntry("investment")}
          >
            Pay Directly from Investment Account
          </Button>
          <Button
            variant="contained"
            onClick={() => handleCreateScheduledJournalEntry("bank")}
          >
            Route through Bank Account
          </Button>
        </DialogActions>
      </Dialog>
      <CreateDisbursementPreviewModal
        open={openDisbursementPreviewDialog}
        onClose={() => setOpenDisbursementPreviewDialog(false)}
        onContinue={handleCreateDisbursement}
        isSubmitting={isCreatingDisbursement}
        charityEin={disbursementPreview?.charityEin ?? ""}
        amount={disbursementPreview?.amount ?? 0}
        purposeNote={disbursementPreview?.purposeNote}
        primaryDonorName={disbursementPreview?.primaryDonorName}
        jointDonorName={disbursementPreview?.jointDonorName}
        donorAddress={disbursementPreview?.donorAddress}
        fundName={disbursementPreview?.fundName}
      />
      <Dialog
        open={disbursementDialog.open}
        onClose={() => setDisbursementDialog(CLOSED_JOURNAL_ENTRY_DIALOG)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{disbursementDialog.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>{disbursementDialog.message}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDisbursementDialog(CLOSED_JOURNAL_ENTRY_DIALOG)}
            autoFocus
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
      <CreateDisbursementPreviewModal
        open={openScheduledDisbursementPreviewDialog}
        onClose={() => setOpenScheduledDisbursementPreviewDialog(false)}
        onContinue={handleCreateScheduledDisbursement}
        isSubmitting={isCreatingScheduledDisbursement}
        charityEin={scheduledDisbursementPreview?.charityEin ?? ""}
        amount={scheduledDisbursementPreview?.amount ?? 0}
        purposeNote={scheduledDisbursementPreview?.purposeNote}
        primaryDonorName={scheduledDisbursementPreview?.primaryDonorName}
        jointDonorName={scheduledDisbursementPreview?.jointDonorName}
        donorAddress={scheduledDisbursementPreview?.donorAddress}
        fundName={scheduledDisbursementPreview?.fundName}
      />
      <Dialog
        open={scheduledDisbursementDialog.open}
        onClose={() =>
          setScheduledDisbursementDialog(CLOSED_JOURNAL_ENTRY_DIALOG)
        }
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{scheduledDisbursementDialog.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {scheduledDisbursementDialog.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setScheduledDisbursementDialog(CLOSED_JOURNAL_ENTRY_DIALOG)
            }
            autoFocus
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Show>
  );
};
